import {
  type KeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import Dropdown from '../atoms/Dropdown';
import Tooltip from '../atoms/Tooltip';

interface KeyPoseTimelineProps {
  maxFrame: number;
  keyPoses?: KeyPoseTimelineKeyPose[];
  onAddKeyPose: (frame: number) => void;
  onAddKeyPoseAtCurrentFrame: () => void;
  canPasteKeyPose?: boolean;
  onCopyFrame?: (frame: number) => void;
  onCopyKeyPose?: (keyPoseId: string) => void;
  onDeleteKeyPose?: (keyPoseId: string) => void;
  onEditKeyPose?: (frame: number, keyPoseId: string) => void;
  onHoverKeyPoseFrameChange?: (frame: number | null) => void;
  onPasteKeyPose?: (frame: number, keyPoseId?: string) => void;
  onSelectKeyPoseFrame?: (frame: number) => void;
  onToggleKeyPoseHidden?: (keyPoseId: string) => void;
}

type KeyPoseTimelineStamp = 'user' | 'interval';

interface KeyPoseTimelineKeyPose {
  id: string;
  frame: number;
  hidden?: boolean;
  stamp?: KeyPoseTimelineStamp;
}

interface KeyPoseMarkersProps {
  canPasteKeyPose: boolean;
  keyPoses: KeyPoseTimelineKeyPose[];
  maxFrame: number;
  onClearEmptyMenuFrame: () => void;
  onClearHoverFrame: () => void;
  onCopyKeyPose?: (keyPoseId: string) => void;
  onDeleteKeyPose?: (keyPoseId: string) => void;
  onEditKeyPose?: (frame: number, keyPoseId: string) => void;
  onOpenKeyPoseMenuChange: (keyPoseId: string | null) => void;
  onPasteKeyPose?: (frame: number, keyPoseId?: string) => void;
  onSelectKeyPoseFrame?: (frame: number) => void;
  onToggleKeyPoseHidden?: (keyPoseId: string) => void;
  openKeyPoseMenuId: string | null;
}

interface KeyPoseMarkerProps
  extends Omit<KeyPoseMarkersProps, 'keyPoses' | 'openKeyPoseMenuId'> {
  isOpen: boolean;
  keyPose: KeyPoseTimelineKeyPose;
}

const MARKER_SIZE = 11;
const MARKER_LINE_WIDTH = 2;
const FRAME_DOT_SIZE = 3;
const FRAME_DOT_MIN_GAP = 4;
const SLIDER_THUMB_SIZE = 20;
const SLIDER_THUMB_RADIUS = SLIDER_THUMB_SIZE / 2;
const HOVER_MARKER_COLOR = 'rgba(255, 128, 37, 0.5)';
const KEY_POSE_MARKER_COLOR = 'rgba(255, 128, 37, 1)';
const INTERVAL_KEY_POSE_MARKER_BORDER_COLOR = 'rgba(255, 176, 102, 1)';
const DIMMED_INTERVAL_KEY_POSE_MARKER_BORDER_COLOR =
  'rgba(255, 176, 102, 0.45)';
const INTERVAL_KEY_POSE_MARKER_BORDER_WIDTH = 2;
const HIDDEN_KEY_POSE_MARKER_CLASS_NAME = 'bg-gray-45 dark:bg-gray-70';
const FRAME_TOOLTIP_DELAY_MS = 75;

function KeyPoseTimeline({
  maxFrame,
  keyPoses = [],
  onAddKeyPose,
  onAddKeyPoseAtCurrentFrame,
  canPasteKeyPose = false,
  onCopyFrame,
  onCopyKeyPose,
  onDeleteKeyPose,
  onEditKeyPose,
  onHoverKeyPoseFrameChange,
  onPasteKeyPose,
  onSelectKeyPoseFrame,
  onToggleKeyPoseHidden,
}: KeyPoseTimelineProps) {
  const [hoverFrame, setHoverFrame] = useState<number | null>(null);
  const [emptyMenuFrame, setEmptyMenuFrame] = useState<number | null>(null);
  const [openKeyPoseMenuId, setOpenKeyPoseMenuId] = useState<string | null>(
    null,
  );
  const activeFrame = hoverFrame ?? 0;
  const hoverFrameKeyPose =
    hoverFrame === null
      ? null
      : getKeyPoseAtFrame(keyPoses, hoverFrame, maxFrame);
  const emptyFrameForMenu =
    emptyMenuFrame !== null &&
    !hasKeyPoseAtFrame(keyPoses, emptyMenuFrame, maxFrame)
      ? emptyMenuFrame
      : null;
  const hoverFramePreview =
    hoverFrame !== null &&
    emptyMenuFrame === null &&
    openKeyPoseMenuId === null &&
    (hoverFrameKeyPose === null || hoverFrameKeyPose.hidden)
      ? hoverFrame
      : null;
  const hoverFrameLabel = formatFrameLabel(activeFrame, maxFrame);

  const clearHoverFrame = useCallback(() => {
    setHoverFrame(null);
    onHoverKeyPoseFrameChange?.(null);
  }, [onHoverKeyPoseFrameChange]);

  const clearEmptyMenuFrame = useCallback(() => {
    setEmptyMenuFrame(null);
  }, []);

  const handleOpenKeyPoseMenuChange = useCallback(
    (keyPoseId: string | null) => {
      setOpenKeyPoseMenuId(keyPoseId);
    },
    [],
  );

  useEffect(() => {
    onHoverKeyPoseFrameChange?.(hoverFramePreview);
  }, [hoverFramePreview, onHoverKeyPoseFrameChange]);

  useEffect(() => {
    return () => onHoverKeyPoseFrameChange?.(null);
  }, [onHoverKeyPoseFrameChange]);

  const getFrameFromPointer = (
    event: MouseEvent<HTMLDivElement> | PointerEvent<HTMLDivElement>,
  ) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const minPointerX = Math.min(SLIDER_THUMB_RADIUS, rect.width);
    const maxPointerX = Math.max(rect.width - SLIDER_THUMB_RADIUS, minPointerX);
    const pointerX = Math.min(
      Math.max(event.clientX - rect.left, minPointerX),
      maxPointerX,
    );
    const usableWidth = Math.max(maxPointerX - minPointerX, 1);

    if (rect.width <= 0 || maxFrame <= 0) {
      return 0;
    }

    return clampFrame(
      Math.round(((pointerX - minPointerX) / usableWidth) * maxFrame),
      maxFrame,
    );
  };

  const handlePointerMove = (event: PointerEvent<HTMLDivElement>) => {
    setHoverFrame(getFrameFromPointer(event));
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    const frame = getFrameFromPointer(event);

    if (!hasKeyPoseAtFrame(keyPoses, frame, maxFrame)) {
      clearHoverFrame();
      onAddKeyPose(frame);
    }
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    event.preventDefault();
    const frame = getFrameFromPointer(event);

    if (!hasKeyPoseAtFrame(keyPoses, frame, maxFrame)) {
      clearHoverFrame();
      setEmptyMenuFrame(frame);
      setOpenKeyPoseMenuId(null);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    clearHoverFrame();
    onAddKeyPoseAtCurrentFrame();
  };

  return (
    <div
      className="relative h-5 w-full"
      data-hover-frame-label={hoverFrameLabel}
      onPointerMove={handlePointerMove}
      onPointerLeave={clearHoverFrame}
      onPointerCancel={clearHoverFrame}
    >
      <div
        role="button"
        tabIndex={0}
        aria-label={`Add key pose at frame ${activeFrame + 1}`}
        className="focus-visible:ring-blue absolute inset-0 z-0 cursor-crosshair rounded-md outline-none focus-visible:ring-2"
        onClick={handleClick}
        onContextMenu={handleContextMenu}
        onKeyDown={handleKeyDown}
      >
        <div
          className="bg-secondary absolute top-1/2 h-[5px] -translate-y-1/2 rounded-full"
          style={{
            left: SLIDER_THUMB_RADIUS - FRAME_DOT_SIZE / 2 - 2,
            right: SLIDER_THUMB_RADIUS - FRAME_DOT_SIZE / 2 - 2,
          }}
        ></div>
        <div
          className="dark:bg-gray-60/40 absolute top-1/2 h-[5px] -translate-y-1/2 rounded-full"
          style={{
            left: SLIDER_THUMB_RADIUS,
            right: SLIDER_THUMB_RADIUS,
          }}
        />
        <FrameDots maxFrame={maxFrame} />
      </div>

      {hoverFramePreview !== null ? (
        <Tooltip
          text={formatFrameLabel(hoverFramePreview, maxFrame)}
          side="top"
          delayDuration={FRAME_TOOLTIP_DELAY_MS}
          open
        >
          <button
            type="button"
            aria-label={`Add key pose at frame ${hoverFramePreview + 1}`}
            className="absolute top-1/2 z-10 cursor-crosshair rounded-full outline-none"
            style={{
              left: getFramePosition(hoverFramePreview, maxFrame),
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              backgroundColor: HOVER_MARKER_COLOR,
              transform: 'translate(-50%, -50%)',
            }}
            onClick={() => {
              clearHoverFrame();
              onAddKeyPose(hoverFramePreview);
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              clearHoverFrame();
              setEmptyMenuFrame(hoverFramePreview);
              setOpenKeyPoseMenuId(null);
            }}
          />
        </Tooltip>
      ) : null}

      {emptyFrameForMenu !== null ? (
        <Dropdown
          align="center"
          side="top"
          sideOffset={2}
          width={240}
          open
          onOpenChange={(open) =>
            setEmptyMenuFrame(open ? emptyFrameForMenu : null)
          }
          trigger={
            <button
              type="button"
              aria-label={`Open key pose actions for frame ${emptyFrameForMenu + 1}`}
              className="focus-visible:ring-blue absolute top-1/2 z-20 flex h-6 w-6 items-center justify-center rounded-full outline-none focus-visible:ring-2"
              style={{
                left: getFramePosition(emptyFrameForMenu, maxFrame),
                transform: 'translate(-50%, -50%)',
              }}
            >
              <span
                className="block rounded-full"
                style={{
                  width: MARKER_SIZE,
                  height: MARKER_SIZE,
                  backgroundColor: HOVER_MARKER_COLOR,
                }}
              />
            </button>
          }
        >
          <Dropdown.Item
            onClick={() => {
              clearHoverFrame();
              onAddKeyPose(emptyFrameForMenu);
              setEmptyMenuFrame(null);
            }}
          >
            Add pose
          </Dropdown.Item>
          <Dropdown.Separator />
          <Dropdown.Item
            onClick={() => {
              clearHoverFrame();
              onCopyFrame?.(emptyFrameForMenu);
              setEmptyMenuFrame(null);
            }}
          >
            Copy pose
          </Dropdown.Item>
          <Dropdown.Item
            disabled={!canPasteKeyPose}
            onClick={
              canPasteKeyPose
                ? () => {
                    clearHoverFrame();
                    onPasteKeyPose?.(emptyFrameForMenu);
                    setEmptyMenuFrame(null);
                  }
                : undefined
            }
          >
            Paste pose
          </Dropdown.Item>
        </Dropdown>
      ) : null}

      <KeyPoseMarkers
        canPasteKeyPose={canPasteKeyPose}
        keyPoses={keyPoses}
        maxFrame={maxFrame}
        onClearEmptyMenuFrame={clearEmptyMenuFrame}
        onClearHoverFrame={clearHoverFrame}
        onCopyKeyPose={onCopyKeyPose}
        onDeleteKeyPose={onDeleteKeyPose}
        onEditKeyPose={onEditKeyPose}
        onOpenKeyPoseMenuChange={handleOpenKeyPoseMenuChange}
        onPasteKeyPose={onPasteKeyPose}
        onSelectKeyPoseFrame={onSelectKeyPoseFrame}
        onToggleKeyPoseHidden={onToggleKeyPoseHidden}
        openKeyPoseMenuId={openKeyPoseMenuId}
      />
    </div>
  );
}

export default memo(KeyPoseTimeline);

const KeyPoseMarkers = memo(function KeyPoseMarkers({
  canPasteKeyPose,
  keyPoses,
  maxFrame,
  onClearEmptyMenuFrame,
  onClearHoverFrame,
  onCopyKeyPose,
  onDeleteKeyPose,
  onEditKeyPose,
  onOpenKeyPoseMenuChange,
  onPasteKeyPose,
  onSelectKeyPoseFrame,
  onToggleKeyPoseHidden,
  openKeyPoseMenuId,
}: KeyPoseMarkersProps) {
  return (
    <>
      {keyPoses.map((keyPose) => (
        <KeyPoseMarker
          key={keyPose.id}
          canPasteKeyPose={canPasteKeyPose}
          isOpen={openKeyPoseMenuId === keyPose.id}
          keyPose={keyPose}
          maxFrame={maxFrame}
          onClearEmptyMenuFrame={onClearEmptyMenuFrame}
          onClearHoverFrame={onClearHoverFrame}
          onCopyKeyPose={onCopyKeyPose}
          onDeleteKeyPose={onDeleteKeyPose}
          onEditKeyPose={onEditKeyPose}
          onOpenKeyPoseMenuChange={onOpenKeyPoseMenuChange}
          onPasteKeyPose={onPasteKeyPose}
          onSelectKeyPoseFrame={onSelectKeyPoseFrame}
          onToggleKeyPoseHidden={onToggleKeyPoseHidden}
        />
      ))}
    </>
  );
});

const KeyPoseMarker = memo(function KeyPoseMarker({
  canPasteKeyPose,
  isOpen,
  keyPose,
  maxFrame,
  onClearEmptyMenuFrame,
  onClearHoverFrame,
  onCopyKeyPose,
  onDeleteKeyPose,
  onEditKeyPose,
  onOpenKeyPoseMenuChange,
  onPasteKeyPose,
  onSelectKeyPoseFrame,
  onToggleKeyPoseHidden,
}: KeyPoseMarkerProps) {
  const frame = clampFrame(keyPose.frame, maxFrame);
  const isIntervalStamp = keyPose.stamp === 'interval';
  const isIntervalHidden = isIntervalStamp && keyPose.hidden;
  const markerClassName =
    !isIntervalStamp && keyPose.hidden ? HIDDEN_KEY_POSE_MARKER_CLASS_NAME : '';
  const markerLineColor = isIntervalStamp
    ? isIntervalHidden
      ? DIMMED_INTERVAL_KEY_POSE_MARKER_BORDER_COLOR
      : INTERVAL_KEY_POSE_MARKER_BORDER_COLOR
    : keyPose.hidden
      ? undefined
      : KEY_POSE_MARKER_COLOR;
  const markerButtonStyle = isIntervalStamp
    ? {
        backgroundColor: 'transparent',
        border: `${INTERVAL_KEY_POSE_MARKER_BORDER_WIDTH}px solid ${
          isIntervalHidden
            ? DIMMED_INTERVAL_KEY_POSE_MARKER_BORDER_COLOR
            : INTERVAL_KEY_POSE_MARKER_BORDER_COLOR
        }`,
        boxSizing: 'border-box' as const,
      }
    : markerLineColor
      ? { backgroundColor: markerLineColor }
      : {};
  const markerAriaLabel = isIntervalStamp
    ? isIntervalHidden
      ? `Hidden interval key pose at frame ${frame + 1}`
      : `Interval key pose at frame ${frame + 1}`
    : keyPose.hidden
      ? `Hidden key pose at frame ${frame + 1}`
      : `Key pose at frame ${frame + 1}`;

  return (
    <div>
      <TimelineMarkerLine
        frame={frame}
        maxFrame={maxFrame}
        color={markerLineColor}
        className={markerClassName}
      />
      <Dropdown
        align="center"
        side="top"
        sideOffset={2}
        triggerTooltip={formatFrameLabel(frame, maxFrame)}
        triggerTooltipDelayDuration={FRAME_TOOLTIP_DELAY_MS}
        width={240}
        open={isOpen}
        onOpenChange={(open) =>
          onOpenKeyPoseMenuChange(open ? keyPose.id : null)
        }
        trigger={
          <button
            type="button"
            aria-label={markerAriaLabel}
            className={`focus-visible:ring-blue absolute top-1/2 z-30 cursor-pointer rounded-full outline-none focus-visible:ring-2 ${markerClassName}`}
            onPointerDown={(event) => {
              event.preventDefault();
            }}
            onClick={() => {
              onClearHoverFrame();
              onClearEmptyMenuFrame();
              onOpenKeyPoseMenuChange(keyPose.id);
            }}
            onDoubleClick={(event) => {
              event.preventDefault();
              onClearHoverFrame();
              onClearEmptyMenuFrame();
              onOpenKeyPoseMenuChange(null);
              onSelectKeyPoseFrame?.(frame);
            }}
            onContextMenu={(event) => {
              event.preventDefault();
              onClearHoverFrame();
              onClearEmptyMenuFrame();
              onOpenKeyPoseMenuChange(keyPose.id);
            }}
            style={{
              left: getFramePosition(frame, maxFrame),
              width: MARKER_SIZE,
              height: MARKER_SIZE,
              ...markerButtonStyle,
              transform: 'translate(-50%, -50%)',
            }}
          />
        }
      >
        <Dropdown.Item
          onClick={() => {
            onClearHoverFrame();
            onEditKeyPose?.(frame, keyPose.id);
            onOpenKeyPoseMenuChange(null);
          }}
        >
          Edit pose
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item
          onClick={() => {
            onClearHoverFrame();
            onCopyKeyPose?.(keyPose.id);
            onOpenKeyPoseMenuChange(null);
          }}
        >
          Copy pose
        </Dropdown.Item>
        <Dropdown.Item
          disabled={!canPasteKeyPose}
          onClick={
            canPasteKeyPose
              ? () => {
                  onClearHoverFrame();
                  onPasteKeyPose?.(frame, keyPose.id);
                  onOpenKeyPoseMenuChange(null);
                }
              : undefined
          }
        >
          Paste pose
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item
          disabled={!onToggleKeyPoseHidden}
          onClick={
            onToggleKeyPoseHidden
              ? () => {
                  onClearHoverFrame();
                  onToggleKeyPoseHidden(keyPose.id);
                  onOpenKeyPoseMenuChange(null);
                }
              : undefined
          }
        >
          {keyPose.hidden ? 'Show pose' : 'Hide pose'}
        </Dropdown.Item>
        <Dropdown.Separator />
        <Dropdown.Item
          onClick={() => {
            onClearHoverFrame();
            onDeleteKeyPose?.(keyPose.id);
            onOpenKeyPoseMenuChange(null);
          }}
          textClassName="text-red-50"
        >
          Remove pose
        </Dropdown.Item>
      </Dropdown>
    </div>
  );
});

const FrameDots = memo(function FrameDots({ maxFrame }: { maxFrame: number }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    const updateContainerWidth = (width: number) => {
      const nextWidth = Math.max(Math.floor(width), 0);
      setContainerWidth((currentWidth) =>
        currentWidth === nextWidth ? currentWidth : nextWidth,
      );
    };
    const measure = () => {
      updateContainerWidth(element.getBoundingClientRect().width);
    };

    measure();

    if (typeof ResizeObserver !== 'undefined') {
      const resizeObserver = new ResizeObserver(([entry]) => {
        updateContainerWidth(entry?.contentRect.width ?? 0);
      });
      resizeObserver.observe(element);

      return () => {
        resizeObserver.disconnect();
      };
    }

    window.addEventListener('resize', measure);

    return () => {
      window.removeEventListener('resize', measure);
    };
  }, []);

  const frameDots = useMemo(
    () =>
      getSampledFrameDots(
        maxFrame,
        getMaxFrameDotCountForWidth(containerWidth),
      ),
    [containerWidth, maxFrame],
  );

  return (
    <div
      ref={containerRef}
      aria-hidden
      className="pointer-events-none absolute inset-0"
    >
      {frameDots.map((frame, index) => (
        <div
          key={frame}
          data-frame-dot={frame}
          className="pointer-events-none absolute top-1/2 rounded-full bg-[#D9D9D9]"
          style={{
            left: getEvenFrameDotPosition(index, frameDots.length),
            width: FRAME_DOT_SIZE,
            height: FRAME_DOT_SIZE,
            transform: 'translate(-50%, -50%)',
          }}
        />
      ))}
    </div>
  );
});

function getMaxFrameDotCountForWidth(containerWidth: number) {
  const usableWidth = Math.max(containerWidth - SLIDER_THUMB_SIZE, 0);
  if (usableWidth < FRAME_DOT_SIZE) {
    return 0;
  }

  return Math.max(
    1,
    Math.floor(
      (usableWidth + FRAME_DOT_MIN_GAP) / (FRAME_DOT_SIZE + FRAME_DOT_MIN_GAP),
    ),
  );
}

function getSampledFrameDots(maxFrame: number, maxDotCount: number) {
  const normalizedMaxFrame = Math.max(Math.round(maxFrame), 0);
  const totalFrameCount = normalizedMaxFrame + 1;
  if (maxDotCount <= 0 || totalFrameCount <= 0) {
    return [] as number[];
  }

  if (totalFrameCount <= maxDotCount) {
    return Array.from({ length: totalFrameCount }, (_, frame) => frame);
  }

  if (maxDotCount === 1) {
    return [0];
  }

  const frames = new Set<number>();
  for (let index = 0; index < maxDotCount; index += 1) {
    frames.add(Math.round((index / (maxDotCount - 1)) * normalizedMaxFrame));
  }

  return Array.from(frames).sort((left, right) => left - right);
}

function getEvenFrameDotPosition(index: number, dotCount: number) {
  if (dotCount <= 1) {
    return getTimelinePositionFromPercent(0);
  }

  return getTimelinePositionFromPercent((index / (dotCount - 1)) * 100);
}

const TimelineMarkerLine = memo(function TimelineMarkerLine({
  frame,
  maxFrame,
  color,
  className = '',
}: {
  frame: number;
  maxFrame: number;
  color?: string;
  className?: string;
}) {
  return (
    <div
      className={`pointer-events-none absolute bottom-[-12.5px] z-20 ${className}`}
      style={{
        left: getFramePosition(frame, maxFrame),
        width: MARKER_LINE_WIDTH,
        height: 20,
        ...(color ? { backgroundColor: color } : {}),
        transform: 'translateX(-50%)',
      }}
    />
  );
});

function clampFrame(frame: number, maxFrame: number) {
  return Math.min(Math.max(Math.round(frame), 0), Math.max(maxFrame, 0));
}

function formatFrameLabel(frame: number, maxFrame: number) {
  return `${clampFrame(frame, maxFrame) + 1} / ${Math.max(maxFrame, 0) + 1}`;
}

function hasKeyPoseAtFrame(
  keyPoses: Array<{ frame: number }>,
  frame: number,
  maxFrame: number,
) {
  return getKeyPoseAtFrame(keyPoses, frame, maxFrame) !== null;
}

function getKeyPoseAtFrame<TKeyPose extends { frame: number }>(
  keyPoses: TKeyPose[],
  frame: number,
  maxFrame: number,
) {
  return (
    keyPoses.find((keyPose) => clampFrame(keyPose.frame, maxFrame) === frame) ??
    null
  );
}

function getFramePercent(frame: number, maxFrame: number) {
  if (maxFrame <= 0) {
    return 0;
  }

  return (clampFrame(frame, maxFrame) / maxFrame) * 100;
}

function getFramePosition(frame: number, maxFrame: number) {
  return getTimelinePositionFromPercent(getFramePercent(frame, maxFrame));
}

function getTimelinePositionFromPercent(framePercent: number) {
  const insetOffset =
    SLIDER_THUMB_RADIUS - (framePercent / 100) * SLIDER_THUMB_SIZE;

  return `calc(${framePercent}% + ${insetOffset}px)`;
}
